import React, { useEffect, useMemo, useState } from 'react';
import { previewUpgrade, upgradeNow, initializeUpgradeCheckout, UpgradePreview } from '../services/paymentsApi';
import { CustomToast } from './customToast';

/* ============================================================================
   PremiumUpgradeFlow — full-screen takeover that converts a pilot org into a
   paying subscriber. Three stages: a feature tour ("the brilliant features"),
   a checkout confirm (real amount from the backend), and a success screen.

   Payment: charges the card already saved during the pilot in one tap
   (paymentsApi.upgradeNow). If there's no saved card or it declines, we fall
   back to Paystack hosted checkout (also the path for M-Pesa). On success we
   call onUpgraded() — the parent flips Pilot→Premium locally; Firestore is the
   source of truth and updates via the org-doc listener too.
   ========================================================================== */

interface Props {
  orgCode: string;
  orgName?: string;
  orgEmail?: string;
  onClose: () => void;
  onUpgraded: () => void;
  /** 'tour' (default) starts on the feature walkthrough; 'checkout' jumps to pay. */
  startAt?: 'tour' | 'checkout';
}

type Phase = 'tour' | 'checkout' | 'processing' | 'paid';

const TOUR = [
  {
    tag: 'Route Intelligence',
    headline: 'Know which routes work — and which are slipping',
    body: 'trasse fingerprints every trip by its start, end and time of day, then scores each route 0–100 for effectiveness. When a route trends slower week over week, you see it before it quietly inflates your fuel bill.',
    stats: [['11', 'routes learned'], ['3', 'time-of-day buckets'], ['+6%', 'faster vs last term']],
    accent: '#2563eb',
  },
  {
    tag: 'Fuel Efficiency',
    headline: 'See every shilling your fuel actually buys',
    body: 'Each bus’s real km-per-litre is measured against an age-adjusted benchmark — older engines get a fairer target, so the score is honest. Cost-per-km and litres-per-100km make a thirsty bus obvious at a glance.',
    stats: [['−8.2%', 'spend, last 30d'], ['Ksh 412k', 'fuel tracked'], ['16,740 L', 'across 8 buses']],
    accent: '#0fa958',
  },
  {
    tag: 'Driver Score Card',
    headline: 'A fair 5-star score for every driver',
    body: 'Every driver earns a rating from four signals — safety, fuel, compliance and timekeeping. Fuel discrepancies and skipped reports cost stars; incidents are flagged for your review, never auto-penalised.',
    stats: [['8', 'drivers rated'], ['4.3', 'fleet average'], ['2', 'need a look']],
    accent: '#7c3aed',
  },
];

const KEYFRAMES = `
@keyframes pufSpin { to { transform: rotate(360deg); } }
@keyframes pufConfetti { 0% { transform: translateY(-12px) rotate(0); opacity: 1; } 100% { transform: translateY(680px) rotate(680deg); opacity: 0; } }
@keyframes pufCheck { 0% { transform: scale(.3); opacity: 0; } 55% { transform: scale(1.18); } 100% { transform: scale(1); opacity: 1; } }
@keyframes pufPanel { from { transform: translateX(22px); opacity: .4; } to { transform: translateX(0); opacity: 1; } }
`;

const PALETTE = ['#2563eb', '#0fa958', '#7c3aed', '#FFA726', '#43BE83'];

function money(amountKES?: number, currency?: string): string {
  if (amountKES == null) return '—';
  const n = Math.round(amountKES).toLocaleString('en-KE');
  return `${currency === 'USD' ? 'USD' : 'KES'} ${n}`;
}

export default function PremiumUpgradeFlow({ orgCode, orgName, orgEmail, onClose, onUpgraded, startAt = 'tour' }: Props) {
  const [phase, setPhase] = useState<Phase>(startAt);
  const [tourStep, setTourStep] = useState(0);
  const [preview, setPreview] = useState<UpgradePreview | null>(null);
  const [hostedBusy, setHostedBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    previewUpgrade(orgCode).then((p) => { if (alive) setPreview(p); });
    return () => { alive = false; };
  }, [orgCode]);

  const subtitle = useMemo(() => {
    if (!preview?.ok) return 'Premium plan';
    const unitLabel = preview.unit === 'vehicle' ? 'vehicles' : 'children';
    const period = preview.cadence === 'monthly' ? 'per month' : preview.cadence === 'yearly' ? 'per year' : 'per term';
    const plan = (preview.plan || 'premium').replace(/^\w/, (c) => c.toUpperCase());
    return `${plan} · ${preview.size ?? ''} ${unitLabel} · ${period}`;
  }, [preview]);

  // Pick saved card → charge it via our backend. On any failure we DON'T silently
  // bounce to Paystack; we toast the reason and let them choose M-Pesa instead.
  async function payWithCard() {
    setPhase('processing');
    const res = await upgradeNow(orgCode);
    if (res.ok) { setPhase('paid'); return; }
    CustomToast.show(res.message || 'Card payment failed — please pick another method.', 'error');
    setPhase('checkout');
  }

  // Pick M-Pesa / different card → the distinct "upgrade" hosted checkout.
  async function payWithMpesa() {
    setHostedBusy(true);
    const res = await initializeUpgradeCheckout(orgCode);
    if (res.ok && res.authorization_url) {
      window.location.href = res.authorization_url;
      return;
    }
    CustomToast.show(res.message || 'Could not open the payment page. Please try again.', 'error');
    setHostedBusy(false);
  }

  function finish() {
    CustomToast.show('Fleet analytics unlocked.', 'success');
    onUpgraded();
  }

  /* ----------------------------------------------------------------- shell -- */
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#eef3f8', display: 'flex', flexDirection: 'column', fontFamily: "'Inter',system-ui,sans-serif", overflow: 'hidden' }}>
      <style>{KEYFRAMES}</style>

      {/* top bar */}
      <div style={{ height: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', borderBottom: '1px solid #e3e9f0', background: '#fff' }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, color: '#64748b' }}>‹ Back to dashboard</button>
        {phase === 'tour' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#64748b' }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'linear-gradient(135deg,#2563eb,#5041BC)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11 }}>P</div>
            PEAR Analytics · <b style={{ color: '#0f172a', fontWeight: 700 }}>Premium preview</b>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: '#64748b' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#0fa958', display: 'inline-block' }} />Secured by <b style={{ color: '#011b33', fontWeight: 800 }}>Paystack</b>
          </div>
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflow: 'auto' }}>
        {phase === 'tour' ? renderTour() : renderCheckout()}
      </div>

      {phase === 'paid' && renderConfetti()}
    </div>
  );

  /* ------------------------------------------------------------------ tour -- */
  function renderTour() {
    const step = TOUR[tourStep];
    const last = tourStep === TOUR.length - 1;
    return (
      <div style={{ width: 940, maxWidth: '100%', background: '#fff', borderRadius: 20, boxShadow: '0 30px 70px rgba(15,40,80,.16)', border: '1px solid #e9eef4', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '17px 28px', borderBottom: '1px solid #f1f4f8' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>What you’ll unlock with Premium</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', letterSpacing: '.05em' }}>{tourStep + 1} / {TOUR.length}</div>
        </div>

        <div style={{ padding: '34px 32px 12px', minHeight: 300 }}>
          <div key={tourStep} style={{ display: 'flex', gap: 38, alignItems: 'center', animation: 'pufPanel .45s cubic-bezier(.16,1,.3,1) both', flexWrap: 'wrap' }}>
            <div style={{ width: 360, height: 270, flexShrink: 0, position: 'relative', background: '#f7faff', border: '1px solid #e8eef7', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {renderHero(tourStep, step.accent)}
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: `${step.accent}14`, color: step.accent, padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{step.tag}</div>
              <h2 style={{ margin: '16px 0 12px', fontSize: 25, fontWeight: 800, color: '#0f172a', letterSpacing: '-.02em', lineHeight: 1.2 }}>{step.headline}</h2>
              <p style={{ margin: '0 0 22px', fontSize: 14.5, color: '#5b6675', lineHeight: 1.6 }}>{step.body}</p>
              <div style={{ display: 'flex', gap: 30 }}>
                {step.stats.map(([v, l], i) => (
                  <div key={i}><div style={{ fontSize: 21, fontWeight: 800, color: i === 0 ? step.accent : '#0f172a' }}>{v}</div><div style={{ fontSize: 12, color: '#94a3b8' }}>{l}</div></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 28px 22px', borderTop: '1px solid #f1f4f8' }}>
          <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
            {TOUR.map((_, i) => (
              <span key={i} style={{ width: i === tourStep ? 22 : 8, height: 8, borderRadius: 999, background: i === tourStep ? '#2563eb' : '#dbe2ea', transition: 'all .2s' }} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button onClick={() => setPhase('checkout')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#94a3b8' }}>Skip to upgrade</button>
            <button onClick={() => (tourStep === 0 ? onClose() : setTourStep((s) => s - 1))} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, color: '#475569', padding: '11px 18px' }}>Back</button>
            <button onClick={() => (last ? setPhase('checkout') : setTourStep((s) => s + 1))} style={{ background: '#2563eb', border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, color: '#fff', padding: '11px 20px', boxShadow: '0 6px 16px rgba(37,99,235,.28)' }}>{last ? 'Upgrade to Premium' : 'Next'}</button>
          </div>
        </div>
      </div>
    );
  }

  function renderHero(stepIdx: number, accent: string) {
    if (stepIdx === 0) {
      return (
        <svg viewBox="0 0 360 270" style={{ width: '100%', height: '100%' }}>
          <g stroke="#e6edf6" strokeWidth="1"><line x1="0" y1="68" x2="360" y2="68" /><line x1="0" y1="135" x2="360" y2="135" /><line x1="0" y1="202" x2="360" y2="202" /></g>
          <path d="M40 225 C 120 205, 130 115, 205 105 S 285 60, 320 44" fill="none" stroke={accent} strokeWidth="3.5" strokeLinecap="round" />
          <circle cx="40" cy="225" r="7" fill="#16a34a" /><circle cx="320" cy="44" r="8" fill={accent} />
          <g><rect x="232" y="186" width="112" height="46" rx="9" fill="#0f172a" /><text x="244" y="206" fill="#94a3b8" fontSize="9" fontWeight="700">EFFECTIVENESS</text><text x="244" y="224" fill="#fff" fontSize="16" fontWeight="800">87 / 100</text></g>
        </svg>
      );
    }
    if (stepIdx === 1) {
      return (
        <div style={{ textAlign: 'center' }}>
          <svg viewBox="0 0 240 140" style={{ width: 250 }}>
            <path d="M30 125 A 90 90 0 0 1 210 125" fill="none" stroke="#e6edf6" strokeWidth="16" strokeLinecap="round" />
            <path d="M30 125 A 90 90 0 0 1 188 70" fill="none" stroke={accent} strokeWidth="16" strokeLinecap="round" />
          </svg>
          <div style={{ marginTop: -34, fontSize: 38, fontWeight: 800, color: '#0f172a' }}>3.8</div>
          <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>km / L · target 4.2</div>
        </div>
      );
    }
    return (
      <div style={{ width: '100%', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#64748b,#334155)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>JM</div>
          <div><div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>J. Mwangi</div><div style={{ fontSize: 12, color: '#94a3b8' }}>Route 4 · 218 trips</div></div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}><div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>4.5</div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>of 5.0</div></div>
        </div>
        <div style={{ fontSize: 22, color: '#f59e0b', letterSpacing: 2, marginBottom: 14 }}>★★★★<span style={{ opacity: .4 }}>★</span></div>
        {[['Safety', 92, '#16a34a'], ['Fuel', 85, '#2563eb'], ['Compliance', 78, '#d97706'], ['Timekeeping', 88, '#2563eb']].map(([l, v, c]) => (
          <div key={l as string} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 4 }}><span style={{ color: '#5b6675', fontWeight: 600 }}>{l}</span><span style={{ color: '#0f172a', fontWeight: 700 }}>{v}</span></div>
            <div style={{ height: 7, background: '#e9edf3', borderRadius: 4, overflow: 'hidden' }}><div style={{ height: '100%', width: `${v}%`, background: c as string, borderRadius: 4 }} /></div>
          </div>
        ))}
      </div>
    );
  }

  /* -------------------------------------------------------------- checkout -- */
  function renderCheckout() {
    if (phase === 'paid') {
      return (
        <div style={{ width: 430, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(15,40,80,.16)', border: '1px solid #e9eef4', padding: '38px 30px 30px', textAlign: 'center' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#e8f8ef', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', animation: 'pufCheck .55s cubic-bezier(.16,1,.3,1) both' }}>
            <span style={{ color: '#0fa958', fontSize: 38, fontWeight: 700 }}>✓</span>
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: 21, fontWeight: 800, color: '#0f172a' }}>Payment successful</h2>
          <p style={{ margin: 0, fontSize: 13.5, color: '#64748b' }}>{money(preview?.amountKES, preview?.currency)} · Premium plan active</p>
          <p style={{ margin: '14px 0 24px', fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>{orgName || 'Your fleet'} is now a Premium account. Your fleet analytics are ready — fuel, routes and driver ratings, all unlocked.</p>
          <button onClick={finish} style={{ width: '100%', padding: 14, border: 'none', borderRadius: 12, background: '#2563eb', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 18px rgba(37,99,235,.3)' }}>Continue to Analytics →</button>
        </div>
      );
    }

    const processing = phase === 'processing';
    return (
      <div style={{ width: 430, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(15,40,80,.16)', border: '1px solid #e9eef4', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 26px', borderBottom: '1px solid #f1f4f8' }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#0fa958,#0a7d42)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 16 }}>P</div>
          <div><div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Pearlabs Limited</div><div style={{ fontSize: 12, color: '#94a3b8' }}>{orgEmail || 'billing@pear'}</div></div>
        </div>

        <div style={{ padding: '24px 26px 8px', textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Amount to pay</div>
          <div style={{ fontSize: 34, fontWeight: 800, color: '#0f172a', letterSpacing: '-.02em', marginTop: 6 }}>{money(preview?.amountKES, preview?.currency)}</div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 6 }}>{subtitle}</div>
        </div>

        <div style={{ padding: '14px 26px 22px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', margin: '8px 0 11px' }}>Choose how to pay</div>

          {/* Saved card — billed through our backend; only offered if a card is on file. */}
          {preview?.hasSavedCard && (
            <button
              disabled={processing || hostedBusy || !preview?.ok}
              onClick={payWithCard}
              style={{ width: '100%', padding: '14px 16px', border: 'none', borderRadius: 12, background: processing ? '#0a8d49' : '#0fa958', color: '#fff', fontFamily: 'inherit', fontSize: 14.5, fontWeight: 700, cursor: processing || hostedBusy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 11, boxShadow: '0 6px 18px rgba(15,169,88,.3)' }}
            >
              <span style={{ fontSize: 18 }}>💳</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{processing ? 'Charging your card…' : 'Pay with saved card'}<div style={{ fontSize: 11.5, fontWeight: 500, opacity: .9 }}>{processing ? 'Please wait' : 'Card saved during your pilot · billed instantly'}</div></span>
              {processing && <span style={{ width: 16, height: 16, border: '2.4px solid rgba(255,255,255,.45)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'pufSpin .7s linear infinite' }} />}
            </button>
          )}

          {/* M-Pesa / different card — the distinct "upgrade" hosted checkout. */}
          <button
            onClick={payWithMpesa}
            disabled={hostedBusy || processing || !preview?.ok}
            style={{ width: '100%', marginTop: preview?.hasSavedCard ? 10 : 0, padding: '14px 16px', border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#fff', color: '#0f172a', fontFamily: 'inherit', fontSize: 14.5, fontWeight: 700, cursor: hostedBusy || processing ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 11 }}
          >
            <span style={{ fontSize: 18 }}>📲</span>
            <span style={{ flex: 1, textAlign: 'left' }}>{hostedBusy ? 'Opening payment page…' : 'Pay with M-Pesa or a different card'}<div style={{ fontSize: 11.5, fontWeight: 500, color: '#94a3b8' }}>Secure Paystack page · enter M-Pesa or new card</div></span>
            <span style={{ color: '#94a3b8' }}>→</span>
          </button>

          <div style={{ textAlign: 'center', fontSize: 11.5, color: '#94a3b8', marginTop: 16 }}>256-bit SSL · Secured by <b style={{ color: '#475569', fontWeight: 700 }}>Paystack</b></div>
          <div style={{ textAlign: 'center', fontSize: 11.5, color: '#0fa958', fontWeight: 600, marginTop: 10 }}>Your pilot ends soon — Premium starts immediately.</div>
        </div>
      </div>
    );
  }

  function renderConfetti() {
    const pieces = Array.from({ length: 50 }, (_, i) => {
      const size = 6 + Math.round(Math.random() * 7);
      return (
        <div key={i} style={{ position: 'absolute', top: -14, left: `${(Math.random() * 100).toFixed(2)}%`, width: size, height: size * 0.42, background: PALETTE[i % PALETTE.length], borderRadius: 2, animation: `pufConfetti ${(1.7 + Math.random() * 1.1).toFixed(2)}s ${(Math.random() * 0.5).toFixed(2)}s linear forwards` }} />
      );
    });
    return <div style={{ position: 'fixed', inset: 0, zIndex: 5000, overflow: 'hidden', pointerEvents: 'none' }}>{pieces}</div>;
  }
}
